import React from 'react';
import { FormData, FormField } from './DynamicForm';
import TextFieldWithControl from '~/App/shared/components/Fields/TextFieldWithControl';
import { Control } from 'react-hook-form';
import { useTranslation } from '~/Locale';
import { getFiledPropsFetcher } from './helpers';
import TextAreaWithControl from '~/App/shared/components/Fields/TextAreaWithControl';
import CheckBoxWithControl from '~/App/shared/components/Fields/CheckBoxWithControl';
import StructuredText from '~/App/components/DatoCMS/StructuredText';
import MultipleChoiceField from './MultipleChoiceField';
import LikertField from './LikertField';
import { HelperText } from './styledComponents';

type Props = {
  field: FormField;
  control: Control<FormData>;
};

const GenericFormFieldDispatcher = ({ field, control }: Props) => {
  const { t } = useTranslation();
  const { fieldType, backendKey, helperText, required } = field;

  switch (fieldType) {
    case 'generic-textField': {
      const { textFieldType, textFieldLabel, textFieldPlaceholder } = field;
      const props = {
        control,
        name: backendKey,
        label: textFieldLabel ?? '',
        helperText: helperText ?? undefined,
        placeholder: textFieldPlaceholder ?? undefined,
        type: ['email', 'number'].includes(textFieldType)
          ? textFieldType
          : undefined
      };

      const getExtraProps = () => {
        const propsFetcher = getFiledPropsFetcher(textFieldType);
        if (propsFetcher) {
          const extraProps = propsFetcher({ t, required });
          return {
            rules: extraProps.rules,
            autoComplete: extraProps.autoComplete
          };
        }

        return {
          rules: {
            required: {
              value: required,
              message: t(`This field can't be blank`)
            }
          }
        };
      };

      return (
        <TextFieldWithControl
          {...props}
          {...getExtraProps()}
          required={required}
        />
      );
    }
    case 'generic-textArea': {
      const { textAreaRows, textAreaLabel } = field;
      return (
        <TextAreaWithControl
          name={backendKey}
          label={textAreaLabel ?? ''}
          control={control}
          helperText={helperText ?? undefined}
          rows={textAreaRows}
          rules={{
            required: {
              value: required,
              message: t(`This field can't be blank`)
            }
          }}
          required={required}
        />
      );
    }
    case 'generic-checkbox': {
      const { checkboxLabel } = field;
      return (
        <div>
          <CheckBoxWithControl
            name={backendKey}
            label={
              <StructuredText
                data={checkboxLabel}
                options={{ paragraphElem: 'span' }}
              />
            }
            color="info"
            control={control}
            rules={{ required }}
            required={required}
          />
          {helperText && <HelperText as="span">{helperText}</HelperText>}
        </div>
      );
    }
    case 'generic-multipleChoice': {
      return <MultipleChoiceField field={field} control={control} />;
    }
    case 'generic-likert': {
      return <LikertField field={field} control={control} />;
    }
    default:
      return null;
  }
};

export default GenericFormFieldDispatcher;
